import { useCallback, useEffect, useRef, useState } from "react";
import { AlertTriangle, Ban, CheckCircle2, ScanFace, XCircle } from "lucide-react";
import type { FacialObservationResponse, TotemRoom } from "@centro-cirurgico/contracts";

import { useFacePresenceGate } from "../facial/useFacePresenceGate";
import { subscribeFaceObservation } from "../utils/api";
import { IdentificationCard } from "./IdentificationCard";

const CAMERA_START_TIMEOUT_MS = 12000;
const VIDEO_PLAYBACK_TIMEOUT_MS = 8000;
/** Tempo em que a mensagem "Acesso não autorizado" fica na tela após um resultado `blocked`. */
const BLOCKED_DISPLAY_MS = 6000;
/** Tempo em que o cartão de identificação continua visível depois que a pessoa sai da frente. */
const CARD_LINGER_MS = 5000;
const CANCELLED_DISPLAY_MS = 4000;

class CameraTimeoutError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "CameraTimeoutError";
  }
}

function withTimeout<T>(promise: Promise<T>, timeoutMs: number, message: string): Promise<T> {
  return new Promise((resolve, reject) => {
    const timeout = window.setTimeout(() => reject(new CameraTimeoutError(message)), timeoutMs);
    promise.then(
      (value) => {
        window.clearTimeout(timeout);
        resolve(value);
      },
      (error: unknown) => {
        window.clearTimeout(timeout);
        reject(error);
      }
    );
  });
}

function requestCameraStream(): Promise<MediaStream> {
  if (!window.isSecureContext || !navigator.mediaDevices?.getUserMedia) {
    return Promise.reject(new DOMException("Camera requires HTTPS.", "NotSupportedError"));
  }

  const request = navigator.mediaDevices.getUserMedia({
    video: { facingMode: "user", width: { ideal: 1280 }, height: { ideal: 720 } },
    audio: false
  });

  return new Promise((resolve, reject) => {
    let settled = false;
    const timeout = window.setTimeout(() => {
      settled = true;
      reject(new CameraTimeoutError("Camera permission request timed out."));
    }, CAMERA_START_TIMEOUT_MS);
    request.then(
      (stream) => {
        if (settled) {
          stream.getTracks().forEach((track) => track.stop());
          return;
        }
        settled = true;
        window.clearTimeout(timeout);
        resolve(stream);
      },
      (error: unknown) => {
        if (settled) return;
        settled = true;
        window.clearTimeout(timeout);
        reject(error);
      }
    );
  });
}

async function startVideoPlayback(video: HTMLVideoElement, stream: MediaStream) {
  video.srcObject = stream;
  video.muted = true;
  video.playsInline = true;
  await withTimeout(Promise.resolve(video.play()), VIDEO_PLAYBACK_TIMEOUT_MS, "Camera playback timed out.");
  if (video.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA && video.videoWidth > 0) return;

  await new Promise<void>((resolve, reject) => {
    const cleanup = () => {
      window.clearTimeout(timeout);
      video.removeEventListener("loadeddata", onReady);
      video.removeEventListener("canplay", onReady);
    };
    const onReady = () => {
      if (video.readyState < HTMLMediaElement.HAVE_CURRENT_DATA || video.videoWidth === 0) return;
      cleanup();
      resolve();
    };
    const timeout = window.setTimeout(() => {
      cleanup();
      reject(new CameraTimeoutError("Camera did not provide a video frame."));
    }, VIDEO_PLAYBACK_TIMEOUT_MS);
    video.addEventListener("loadeddata", onReady);
    video.addEventListener("canplay", onReady);
  });
}

function cameraErrorMessage(error: unknown) {
  const name = error instanceof Error ? error.name : "";
  switch (name) {
    case "NotAllowedError":
    case "SecurityError":
      return "A câmera está bloqueada. Autorize o acesso no navegador e tente novamente.";
    case "NotFoundError":
    case "OverconstrainedError":
      return "Nenhuma câmera compatível foi encontrada neste dispositivo.";
    case "NotReadableError":
    case "AbortError":
      return "A câmera está ocupada por outro aplicativo.";
    case "NotSupportedError":
      return "Abra o totem em uma conexão HTTPS para usar a câmera.";
    case "CameraTimeoutError":
      return "A câmera demorou para responder. Verifique o dispositivo.";
    default:
      return "Não foi possível iniciar a câmera.";
  }
}

type StatusTone = "idle" | "success" | "warning" | "blocked" | "error";

function StatusIcon({ tone }: { tone: StatusTone }) {
  const props = { "aria-hidden": true as const, size: 36 };
  switch (tone) {
    case "success":
      return <CheckCircle2 {...props} />;
    case "warning":
      return <AlertTriangle {...props} />;
    case "blocked":
      return <Ban {...props} />;
    case "error":
      return <XCircle {...props} />;
    default:
      return <ScanFace {...props} />;
  }
}

export interface ClinicalFlowProps {
  room: TotemRoom;
  /** Dentro da tela da sala (troca rápida pulseira ↔ rosto): texto mais curto. */
  embedded?: boolean;
}

export function ClinicalFlow({ room, embedded = false }: ClinicalFlowProps) {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const cameraAttemptRef = useRef(0);
  const blockedTimerRef = useRef<number | null>(null);
  const [cameraFailed, setCameraFailed] = useState(false);
  const [cameraError, setCameraError] = useState<unknown>(null);
  const [cameraReady, setCameraReady] = useState(false);
  const [blocked, setBlocked] = useState<FacialObservationResponse | null>(null);

  const {
    state: gateState,
    message: gateMessage,
    result,
    holdProgress,
    holdCancelled,
    detectorError,
    isRetryable,
    retry: retryGate
  } = useFacePresenceGate(room.id, videoRef, cameraReady);

  // Cartão de identificação: acompanha o resultado atual e continua visível por alguns
  // segundos depois que a pessoa se afasta (o hook limpa o resultado na ausência).
  const [card, setCard] = useState<FacialObservationResponse | null>(null);
  const [cardCancelled, setCardCancelled] = useState(false);
  const cardTimerRef = useRef<number | null>(null);
  useEffect(() => {
    if (cardTimerRef.current !== null) {
      window.clearTimeout(cardTimerRef.current);
      cardTimerRef.current = null;
    }
    const shown = result && (result.status === "recognized" || result.status === "blocked") ? result : null;
    if (shown && !holdCancelled) {
      setCard(shown);
      setCardCancelled(false);
      return;
    }
    if (holdCancelled) setCardCancelled(true);
    cardTimerRef.current = window.setTimeout(
      () => {
        cardTimerRef.current = null;
        setCard(null);
        setCardCancelled(false);
      },
      holdCancelled ? CANCELLED_DISPLAY_MS : CARD_LINGER_MS
    );
  }, [result, holdCancelled]);
  useEffect(
    () => () => {
      if (cardTimerRef.current !== null) window.clearTimeout(cardTimerRef.current);
    },
    []
  );

  // O hook facial descarta respostas `blocked` (4.5); observamos a API diretamente para exibi-las.
  useEffect(() => {
    const unsubscribe = subscribeFaceObservation((response) => {
      if (response.status !== "blocked") return;
      setBlocked(response);
      if (blockedTimerRef.current !== null) window.clearTimeout(blockedTimerRef.current);
      blockedTimerRef.current = window.setTimeout(() => {
        blockedTimerRef.current = null;
        setBlocked(null);
      }, BLOCKED_DISPLAY_MS);
    });
    return () => {
      unsubscribe();
      if (blockedTimerRef.current !== null) window.clearTimeout(blockedTimerRef.current);
    };
  }, []);

  const stopCamera = useCallback(() => {
    cameraAttemptRef.current += 1;
    const video = videoRef.current;
    if (video) {
      video.pause();
      video.srcObject = null;
    }
    streamRef.current?.getTracks().forEach((track) => track.stop());
    streamRef.current = null;
    setCameraReady(false);
  }, []);

  const startCamera = useCallback(async () => {
    stopCamera();
    const attempt = cameraAttemptRef.current;
    setCameraFailed(false);
    setCameraError(null);
    setCameraReady(false);

    try {
      const stream = await requestCameraStream();
      if (attempt !== cameraAttemptRef.current) {
        stream.getTracks().forEach((track) => track.stop());
        return;
      }
      const video = videoRef.current;
      if (!video) {
        stream.getTracks().forEach((track) => track.stop());
        throw new Error("Camera video element is unavailable.");
      }
      streamRef.current = stream;
      await startVideoPlayback(video, stream);
      if (attempt !== cameraAttemptRef.current) return;

      stream.getVideoTracks().forEach((track) =>
        track.addEventListener("ended", () => {
          if (streamRef.current !== stream) return;
          setCameraFailed(true);
          setCameraReady(false);
        })
      );

      setCameraReady(true);
    } catch (error) {
      if (attempt !== cameraAttemptRef.current) return;
      stopCamera();
      setCameraError(error);
      setCameraFailed(true);
    }
  }, [stopCamera]);

  useEffect(() => {
    void startCamera();
    return stopCamera;
  }, [startCamera, stopCamera]);

  const handleRetry = useCallback(() => {
    if (cameraFailed) {
      void startCamera();
    } else if (isRetryable) {
      retryGate();
    }
  }, [cameraFailed, isRetryable, startCamera, retryGate]);

  const recognized = !blocked && result?.status === "recognized";
  const holding = gateState === "holding";
  const unknown = !blocked && result?.status === "unrecognized";
  const showError = cameraFailed || gateState === "error";
  const errorMessage = cameraFailed ? cameraErrorMessage(cameraError) : (detectorError ?? "Erro no reconhecimento.");

  const tone: StatusTone = blocked
    ? "blocked"
    : showError
      ? "error"
      : recognized
        ? "success"
        : unknown
          ? "warning"
          : "idle";

  // Estado visual da câmera (anel + faixa de varredura).
  const uiState = cameraFailed
    ? "error"
    : blocked
      ? "blocked"
      : gateState === "identified"
        ? "recorded"
        : holding
          ? "holding"
          : gateState === "recognizing"
          ? "recognizing"
          : gateState === "error"
            ? "error"
            : "scanning";

  // Resultado: somente nome de exibição + categoria profissional (4.5 / 5.10).
  const professional = (response: FacialObservationResponse | null) =>
    response?.professionalName
      ? `${response.professionalName} · ${response.roleLabel ?? "Profissional"}`
      : (response?.roleLabel ?? "Profissional");

  const title = blocked
    ? "Acesso não autorizado. Procure a coordenação."
    : showError
      ? errorMessage
      : gateMessage;

  const detail = blocked
    ? professional(blocked)
    : holding
      ? "Fique parado até o círculo completar para registrar a saída."
      : recognized
      ? professional(result)
      : unknown
        ? "Rosto não reconhecido. Tente novamente ou procure a coordenação."
        : showError
          ? cameraFailed
            ? "Confira a permissão do navegador e a conexão da câmera."
            : "Toque em tentar novamente para reiniciar o reconhecimento."
          : `Sala monitorada: ${room.name}`;

  return (
    <section className={`totem-shell state-${uiState}${embedded ? " embedded" : ""}${card ? " has-card" : ""}`}>
      <div className="totem-copy">
        <p className="eyebrow">Entrada e saída do centro cirúrgico</p>
        <h1>{embedded ? "Valide seu rosto" : "Identifique-se na entrada e na saída"}</h1>
        <p className="lead">
          Olhe para a câmera: a primeira validação registra a <strong>entrada</strong>. Na saída, valide de novo e
          fique parado por alguns segundos até o círculo completar.
        </p>
      </div>

      <div className="camera-stage" aria-label="Área de reconhecimento facial">
        <video ref={videoRef} className="camera-video" autoPlay playsInline muted />
        <div className="face-ring" aria-hidden="true" />
        <div className="scan-band" aria-hidden="true" />
        {(!cameraReady || cameraFailed) && (
          <div className="camera-stage-message">{cameraFailed ? "Câmera indisponível" : "Preparando câmera"}</div>
        )}
        {card && (
          <IdentificationCard
            key={`${card.professionalId ?? "x"}-${card.action ?? card.status}-${cardCancelled ? "c" : ""}`}
            response={card}
            holdProgress={holding ? holdProgress : null}
            cancelled={cardCancelled}
          />
        )}
      </div>

      <div className={`status-panel tone-${tone}`} aria-live="polite" aria-atomic="true">
        <div className={`status-mark ${tone}`}>
          <StatusIcon tone={tone} />
        </div>
        <div className="status-copy">
          <span className="status-title">{title}</span>
          <span className="status-detail">{detail}</span>
        </div>
        {showError && !blocked && (
          <button type="button" className="primary big-button retry-button" onClick={handleRetry}>
            {cameraFailed ? "Ativar câmera" : "Tentar novamente"}
          </button>
        )}
      </div>
    </section>
  );
}
